import type { Maybe } from '@adornis/base/utilTypes.js';
import type { Form } from '@adornis/forms/form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import { getPossibleKlassenForSchoolTypeAbstract } from '../../../groups/api/getPossibleKlassenForSchoolType.js';
import { SchoolType } from '../../../groups/entities/School.js';
import { RegisterMode } from '../../registrationMode.js';
import type { RegisterStudentData } from '../api/registerStudentWithSuSForm.js';
import { searchSchoolNames } from '../api/searchSchoolNames.js';
import { RegistrationField } from './fields/registration-field.js';

export const SgSusEntryFormSchoolData = stateful(
  (props: {
    form: Form<RegisterStudentData>;
    school: Maybe<{ _id: string; schoolType: Maybe<SchoolType> }>;
    mode: RegisterMode;
  }) => {
    const {
      value: schools,
      loading,
      error,
    } = useQuery(() => GroupsAPI.getPublicSchools(true)({ _id: 1, name: 1, schoolType: 1 }), []);
    const transition = props.mode === RegisterMode.REGISTRATION_ELEMENTARY_HIGH;
    const elementary = transition || props.mode === RegisterMode.REGISTRATION_ELEMENTARY_ELEMENTARY;
    const selectable = (schools ?? []).filter(
      s =>
        (elementary ? s.schoolType === SchoolType.GRUNDSCHULE : s.schoolType !== SchoolType.GRUNDSCHULE) &&
        s._id !== props.school?._id,
    );
    const field = (name: string, options: Parameters<typeof RegistrationField>[2]) =>
      RegistrationField(props.form, [name], options);
    return html`<section class="card w-full">
      <div class="card-body gap-5 p-4 sm:p-6">
        <h2 class="font-semibold md:text-lg">${transition ? 'Bisherige Grundschule' : 'Derzeitige Schule'}</h2>
        ${loading ? html`<p role="status">Schulen werden geladen …</p>` : nothing}
        ${error ? html`<p role="alert" class="alert alert-warning alert-soft text-base-content">Die Schulliste konnte nicht geladen werden. Sie können den Namen unter „Andere Schule“ selbst eintragen.</p>` : nothing}
        <div class="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          ${field('potentialMartinusElementarySchoolID', { label: 'Besuchte Schule', required: true, options: [...selectable.map(s => ({ value: s._id, label: s.name ?? 'Schule' })), 'Andere Schule'], onPicked: value => props.form.pickValueForField(['potentialMartinusElementarySchoolName'], selectable.find(s => s._id === value)?.name ?? '') })}
          ${props.form.document?.potentialMartinusElementarySchoolID === 'Andere Schule' ? field('potentialMartinusElementarySchoolName', { label: 'Name der Schule', required: true, search: query => searchSchoolNames(query)() }) : nothing}
          ${transition ? field('schoolyearsAfterElementary', { label: 'Schulbesuchsjahre bis zum Ende der Grundschule', required: true, options: [3, 4, 5] }) : field('currentKlassenstufe', { label: 'Aktuelle Jahrgangsstufe', required: true, options: getPossibleKlassenForSchoolTypeAbstract(props.school?.schoolType) })}
          ${transition ? html`${field('lastGradeGerman', { label: 'Letzte Deutschnote der 3. Klasse', options: [1, 2, 3, 4, 5, 6] })}${field('lastGradeMath', { label: 'Letzte Mathematiknote der 3. Klasse', options: [1, 2, 3, 4, 5, 6] })}${field('lastGradeSachunterricht', { label: 'Letzte Sachunterrichtsnote der 3. Klasse', options: [1, 2, 3, 4, 5, 6] })}` : nothing}
        </div>
      </div>
    </section>`;
  },
);
